import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import LisLabLogo from './LisLabLogo';

const NAV_LINKS = [
  { label: 'الرئيسية', to: '/' },
  { label: 'الباقات', to: '/pricing' },
  { label: 'من نحن', to: '/about' },
];

export default function LandingNavbar({ transparent = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!transparent) return;
    const handle = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, [transparent]);

  const solidBg = !transparent || scrolled;

  return (
    <div className="fixed top-0 right-0 left-0 z-50" style={{ fontFamily: "'Readex Pro', 'Tajawal', sans-serif" }}>
      {/* Promo Banner */}
      <div className="bg-[#0F6E56] text-white text-center py-1.5 sm:py-2 px-4 text-[11px] sm:text-[13px] font-medium flex items-center justify-center gap-3">
        <span>🔥 عرض محدود — خصم 60% على الباقة المتقدمة</span>
        <Link to="/register" className="underline underline-offset-2 hover:text-emerald-200 transition-colors whitespace-nowrap">سجّل الآن</Link>
      </div>
    <nav
      className={`transition-all duration-300 ${
        solidBg
          ? 'bg-white/80 backdrop-blur-xl border-b border-[#E8E5DD]/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-[4.25rem]">

          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/listech-logo.svg" alt="LIS Techs" className="h-12 md:h-14" />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map(link => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                    isActive
                      ? 'text-[#0F6E56] bg-[#0F6E56]/[0.06]'
                      : 'text-[#4A4840] hover:text-[#0F6E56] hover:bg-[#0F6E56]/[0.04]'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="text-[13px] font-medium text-[#4A4840] hover:text-[#0F6E56] px-4 py-2 transition-colors"
            >
              تسجيل الدخول
            </Link>
            <Link
              to="/register"
              className="text-[13px] font-semibold text-white px-5 py-2.5 rounded-lg transition-all hover:shadow-md hover:scale-[1.02] active:scale-[0.98] bg-[#0F6E56]"
            >
              ابدأ مجانًا
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenu(v => !v)}
            className="md:hidden p-2 text-[#1A1A18] hover:bg-[#F6F5F0] rounded-lg transition-colors"
            aria-label="القائمة"
          >
            {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenu && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-[#E8E5DD] overflow-hidden"
          >
            <div className="px-6 py-4 space-y-1">
              {NAV_LINKS.map(link => {
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileMenu(false)}
                    className={`block px-4 py-3 text-[14px] font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'text-[#0F6E56] bg-[#0F6E56]/[0.06]'
                        : 'text-[#1A1A18] hover:bg-[#F6F5F0] hover:text-[#0F6E56]'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="pt-3 border-t border-[#E8E5DD] space-y-2">
                <Link
                  to="/login"
                  onClick={() => setMobileMenu(false)}
                  className="block text-center px-4 py-3 text-[14px] font-medium text-[#4A4840] hover:bg-[#F6F5F0] rounded-lg"
                >
                  تسجيل الدخول
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenu(false)}
                  className="block text-center px-4 py-3 text-[14px] font-semibold text-white rounded-lg bg-[#0F6E56]"
                >
                  ابدأ مجانًا
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    </div>
  );
}
